import { useScreenCue } from "./ScreenCue";
import { useExit } from "./useExit";
import { PixelIcon } from "./PixelArt";
import { createPortal } from "preact/compat";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { useEscape } from "../escape";
import { COMPARE_WORDS, compareKey, comparePercent, compareSnap, compareStart } from "../../shared/compare.ts";

interface Props {
  before: string;
  after: string;
  onClose: () => void;
}

/** Room kept around the photos: the close button's row above, a little below. */
/** Half the handle's width, so at either edge the whole handle stays on screen. */
const PAD_X = 20;
const PAD_TOP = 64;
const PAD_BOTTOM = 24;

/**
 * The photo viewer in compare mode (flag `compareUI`, shared/compare.ts): the before and the after
 * the same size, one over the other, and a bronze handle on a pixel divider that the thumb drags
 * left and right to wipe between them; before on the left of the divider, after on the right. The
 * handle lands on 4px steps with no easing and starts in the middle. It opens and closes as the
 * viewer does (src/components/Lightbox.tsx): the same black, the same X, Escape, the stepped fade.
 */
export function CompareLightbox({ before, after, onClose }: Props) {
  const exit = useExit(onClose);
  useScreenCue(exit.closing);
  const closeRef = useRef<HTMLButtonElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  /** The before photo's shape, which both photos share; 3:4 until it has loaded. */
  const [ratio, setRatio] = useState(3 / 4);
  const [stage, setStage] = useState({ w: 0, h: 0 });
  const fitW = stage.w > 0 ? Math.floor(Math.min(stage.w - PAD_X * 2, (stage.h - PAD_TOP - PAD_BOTTOM) * ratio)) : 0;
  const box = { w: Math.max(0, fitW), h: Math.max(0, Math.floor(fitW / ratio)) };
  /** The divider's place as a share of the width, so a turn of the phone keeps it where it was. */
  const [share, setShare] = useState<number | null>(null);
  const pos = share === null ? compareStart(box.w) : compareSnap(share * box.w, box.w);
  const dragging = useRef<number | null>(null);
  /** The handle's place and the box's width, for the viewer's key listener (registered once). */
  const posRef = useRef({ pos, w: box.w });
  posRef.current = { pos, w: box.w };

  useLayoutEffect(() => {
    const measure = () => {
      const s = stageRef.current;
      if (s) setStage({ w: s.clientWidth, h: s.clientHeight });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  function moveTo(clientX: number) {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect || !box.w) return;
    const next = compareSnap(clientX - rect.left, box.w);
    setShare(next / box.w);
  }

  useEscape(!exit.closing, () => exit.close());

  useEffect(() => {
    const previousFocus = document.activeElement;
    closeRef.current?.focus();
    // Two stops for Tab, the close and the handle, and nothing behind the viewer.
    const onKey = (e: KeyboardEvent) => {
      // The arrows (and Home, End) wipe from anywhere in the viewer, not only once
      // Tab has reached the handle: the focus opens on the X. The handle takes the focus with the
      // first press, so its own keys answer from then on and a screen reader hears the value.
      if (e.key !== "Tab") {
        const handle = handleRef.current;
        if (!handle || document.activeElement === handle || e.ctrlKey || e.metaKey || e.altKey) return;
        const { pos: at, w } = posRef.current;
        const next = compareKey(at, w, e.key);
        if (next === null || !w) return;
        e.preventDefault();
        handle.focus();
        setShare(next / w);
        return;
      }
      e.preventDefault();
      (document.activeElement === closeRef.current ? handleRef.current : closeRef.current)?.focus();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, []);

  const percent = comparePercent(pos, box.w);
  return createPortal(
    <div class={`lightbox lightbox-compare${exit.cls}`} ref={exit.ref} role="dialog" aria-modal="true" aria-label={COMPARE_WORDS.slider}>
      <button ref={closeRef} class="lightbox-close" type="button" onClick={() => exit.close()} aria-label="Close">
        <PixelIcon name="close" />
      </button>
      {/* The whole stage takes the pointer, captured, so a touch beside the photos or a finger that
          runs off them still moves the handle (owner, 2026-10-05: "sometimes gets stuck at the edges"). */}
      <div
        ref={stageRef}
        class="compare-stage"
        onPointerDown={(e) => {
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          dragging.current = e.pointerId;
          moveTo(e.clientX);
        }}
        onPointerMove={(e) => {
          if (dragging.current === e.pointerId) moveTo(e.clientX);
        }}
        onPointerUp={(e) => {
          if (dragging.current === e.pointerId) moveTo(e.clientX);
          dragging.current = null;
        }}
        onPointerCancel={() => (dragging.current = null)}
      >
        {box.w > 0 && (
          <div ref={boxRef} class="compare-box" style={{ width: `${box.w}px`, height: `${box.h}px` }}>
            <img class="compare-photo" src={after} alt="After" draggable={false} />
            <img
              class="compare-photo compare-before"
              src={before}
              alt="Before"
              draggable={false}
              style={{ clipPath: `inset(0 ${box.w - pos}px 0 0)` }}
              onLoad={(e) => {
                const img = e.currentTarget;
                if (img.naturalWidth > 0 && img.naturalHeight > 0) setRatio(img.naturalWidth / img.naturalHeight);
              }}
            />
            <span class="compare-caption compare-caption-before" aria-hidden="true">Before</span>
            <span class="compare-caption compare-caption-after" aria-hidden="true">After</span>
            <div class="compare-divider" style={{ transform: `translateX(${pos}px)` }}>
              <div
                ref={handleRef}
                class="compare-handle"
                role="slider"
                tabIndex={0}
                aria-label={COMPARE_WORDS.slider}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
                aria-valuetext={COMPARE_WORDS.value(percent)}
                onKeyDown={(e) => {
                  const next = compareKey(pos, box.w, e.key);
                  if (next === null || !box.w) return;
                  e.preventDefault();
                  setShare(next / box.w);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
